import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useAdParameters } from '../../common/creative/hooks/useAdParameters';
import { useAdSize } from '../../common/creative/hooks/useAdSize';
import { useControls } from '../../common/creative/hooks/useControls';
import { useManualDuration } from '../../common/creative/hooks/useManualDuration';
import { useSetAdBackgroundColor } from '../../common/creative/hooks/useSetAdBackgroundColor';
import { useSetAdIsDisplay } from '../../common/creative/hooks/useSetAdIsDisplay';
import { useVPAIDPlayState } from '../../common/creative/hooks/useVPAIDPlayState';
import { onTeadsApiAvailable, sendVideoMetadata, teadsApi } from '../../common/teads-api';
import { MPUTagConfig } from './config';
import { MPUTagCreative } from './creative';

function tryAddTrackerToDCMTag(doc: Document, tracker: string): boolean {
  const insTag = doc.querySelector('ins');

  if (insTag?.hasAttribute('data-dcm-placement')) {
    insTag.setAttribute('data-dcm-click-tracker', tracker);
    return true;
  }

  return false;
}

function tryAddTrackerToSizmekTag(doc: Document, tracker: string, teadsClickPixel: string): boolean {
  const tag = doc.querySelector('script');
  const link = doc.querySelector('a');

  if (tag?.hasAttribute('src') && link?.hasAttribute('href')) {
    let tagUrl = tag.getAttribute('src')!;
    tagUrl += '&ncu=' + encodeURIComponent(teadsClickPixel);
    tag.setAttribute('src', tagUrl);

    let linkUrl = link.getAttribute('href');
    linkUrl = tracker + linkUrl;
    link.setAttribute('href', linkUrl);

    return true;
  }

  return false;
}

function getClickRedirect(studioTagUri: string, clickPixels: string[]) {
  const clickPixelsQueryString = clickPixels.map((pixel) => `c=${encodeURIComponent(pixel)}`).join('&');

  return `${studioTagUri}click-redirect?${clickPixelsQueryString}&u=`;
}

function getTeadsClickPixel(clickPixels: string[]) {
  return clickPixels.find((cp) => /^https?:\/\/t\.teads\.tv/.test(cp));
}

function addClickTracker(tag: string, studioTagUri: string, clickPixels: string[]): string {
  const decodedTag = decodeURIComponent(tag);
  const doc = new DOMParser().parseFromString(`<body>${decodedTag}</body>`, 'text/html');

  const tracker = getClickRedirect(studioTagUri, clickPixels);
  const teadsClickPixel = getTeadsClickPixel(clickPixels);

  tryAddTrackerToDCMTag(doc, tracker) || tryAddTrackerToSizmekTag(doc, tracker, teadsClickPixel || '');

  return encodeURIComponent(doc.body.innerHTML);
}

export function MPUTag() {
  const {
    config,
    env: { studio_tag_uri },
  } = useAdParameters<MPUTagConfig>();
  const { updateTime, setAdLoaded } = useControls();
  useSetAdBackgroundColor('#fff');
  useSetAdIsDisplay(true);
  useEffect(() => {
    teadsApi.hideVideoContainer();
  }, []);

  const { playState } = useVPAIDPlayState();
  useManualDuration(playState.isPlaying, 15, updateTime);

  const adSize = useAdSize();

  const size = useMemo(
    () => ({
      width: config.width ?? 300,
      height: config.height ?? 250,
    }),
    [config.width, config.height]
  );

  useEffect(() => {
    sendVideoMetadata({
      width: adSize.width,
      height: size.height,
    });
    setAdLoaded();
  }, [adSize.width, size.height]);

  const [tag, setTag] = useState('');

  useEffect(() => {
    teadsApi.getTeadsInfo((e, response) => {
      setTag(
        e || !response || !response.clickPixels || !response.clickPixels.length
          ? config.tag
          : addClickTracker(config.tag, studio_tag_uri, response.clickPixels)
      );
    });

    const listener = (available: boolean) => {
      if (!available) {
        setTag(config.tag);
      }
    };

    onTeadsApiAvailable.subscribe(listener);
    return () => onTeadsApiAvailable.unsubscribe(listener);
  }, []);

  return <MPUTagCreative size={size} tag={tag} />;
}
